import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { getArticleById } from '@/mocks/articles';
import { getComicById, getPopularComics } from '@/mocks/comics';
import { categoryMap } from '@/mocks/categories';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = id ? parseInt(id, 10) : 0;
  const article = getArticleById(articleId);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
              <i className="ri-question-line text-4xl text-primary-500"></i>
            </div>
            <h1 className="text-xl font-heading text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-4">抱歉，你查找的文章不存在或已被删除</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-medium transition-colors whitespace-nowrap">
              <i className="ri-arrow-left-line"></i>返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const comic = getComicById(article.comicId);
  const relatedComics = article.relatedComicIds
    .map((cid) => getComicById(cid))
    .filter((c): c is NonNullable<typeof c> => c !== undefined);
  const popularComics = getPopularComics(4).filter((c) => c.id !== article.comicId).slice(0, 3);
  const category = categoryMap[article.category];

  const pageTitle = `${article.title} - 星球漫画`;
  const pageDescription = article.description.substring(0, 160);
  const pageKeywords = article.tags.map((t) => `免费${t}漫画`).join(',') + `,${article.title},星球漫画`;

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    dateModified: article.updateDate,
    image: article.coverImage,
    publisher: {
      '@type': 'Organization',
      name: '星球漫画',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.xingqiumanhua.com/article/${article.id}`,
    },
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />
      <link rel="canonical" href={`https://www.xingqiumanhua.com/article/${article.id}`} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={`https://www.xingqiumanhua.com/article/${article.id}`} />
      <meta property="og:image" content={article.coverImage} />
      <meta property="article:published_time" content={article.publishDate} />
      <meta property="article:modified_time" content={article.updateDate} />
      <meta property="article:author" content={article.author} />
      {article.tags.map((tag) => (
        <meta key={tag} property="article:tag" content={tag} />
      ))}
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="max-w-4xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              ...(category ? [{ label: `${category.name}漫画`, href: `/category/${category.slug}` }] : []),
              ...(comic ? [{ label: comic.title }] : []),
              { label: article.title },
            ]}
          />

          <article className="px-4 md:px-6 py-6 md:py-8">
            <div className="mb-8">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                {article.tags.map((tag) => (
                  <span key={tag} className="text-xs bg-accent-100 text-accent-700 px-2.5 py-1 rounded-full whitespace-nowrap">
                    {tag}
                  </span>
                ))}
                <span className="text-xs text-foreground-400">
                  <i className="ri-time-line mr-1"></i>{article.publishDate}
                </span>
                <span className="text-xs text-foreground-400">
                  <i className="ri-eye-line mr-1"></i>{(article.views / 10000).toFixed(1)}万阅读
                </span>
              </div>
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-3 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-sm font-medium">
                  {article.author[0]}
                </div>
                <span className="text-sm text-foreground-600">{article.author}</span>
                <span className="text-xs text-foreground-400">评分 {article.rating}</span>
              </div>
            </div>

            <div className="w-full aspect-[2/1] rounded-lg overflow-hidden mb-8">
              <img
                src={article.coverImage}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="prose-custom max-w-none">
              {article.sections.map((section, index) => {
                if (section.type === 'heading') {
                  const HeadingTag = section.level === 2 ? 'h2' : 'h3';
                  return (
                    <HeadingTag
                      key={index}
                      className={`font-heading text-foreground-900 mt-8 mb-4 ${
                        section.level === 2 ? 'text-lg md:text-xl' : 'text-base md:text-lg'
                      }`}
                    >
                      {section.content}
                    </HeadingTag>
                  );
                }
                if (section.type === 'image') {
                  return (
                    <div key={index} className="w-full aspect-[2/1] rounded-lg overflow-hidden my-6">
                      <img
                        src={section.content}
                        alt={article.title}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  );
                }
                return (
                  <p key={index} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    {section.content}
                  </p>
                );
              })}
            </div>

            {comic && (
              <div className="mt-10 p-5 bg-primary-50 rounded-lg border border-primary-100">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-20 flex-shrink-0 rounded overflow-hidden">
                    <img src={comic.coverImage} alt={comic.title} className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <Link to={`/article/${comic.id}`} className="font-heading text-base text-primary-700 hover:text-primary-800 transition-colors">
                      继续阅读《{comic.title}》
                    </Link>
                    <p className="text-xs text-foreground-500 mt-1">{comic.author} · {comic.status} · {comic.chapters.length}话</p>
                    <span className="inline-flex items-center gap-1 text-xs text-primary-600 mt-1">
                      <i className="ri-arrow-right-line"></i>点击进入完整阅读
                    </span>
                  </div>
                </div>
              </div>
            )}
          </article>

          {relatedComics.length > 0 && (
            <section className="px-4 md:px-6 pb-8">
              <h3 className="text-lg font-heading text-foreground-900 mb-4">
                <i className="ri-links-line text-primary-500 mr-2"></i>相关推荐
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} comic={c} size="normal" />
                ))}
              </div>
            </section>
          )}

          {popularComics.length > 0 && (
            <section className="px-4 md:px-6 pb-10">
              <h3 className="text-lg font-heading text-foreground-900 mb-4">
                <i className="ri-fire-line text-primary-500 mr-2"></i>热门漫画
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {popularComics.map((c) => (
                  <ComicCard key={c.id} comic={c} size="normal" />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}